import { AxiosError } from 'axios';
import { createContext, Dispatch, SetStateAction } from 'react';
import { TAccessPolicy } from '@ocm_customer/account_pro-ui-access';
import { IAlert, TDateSelectValues } from '../models/common';
import { IOrderDetailsResponse } from '../models/order-details';
import {
  ILDRequest,
  IOrderHistoryResponse,
  IPHFilterRequest,
  IPHRequest,
} from '../models/purchase-history';
import { DateRanges } from '../utils/DateRanges';

export interface IPurchaseHistoryContext {
  alertState: IAlert | null;
  localDeliveryAlertState: IAlert | null;
  customerRole: string;
  customerType: string;
  cpsDataSource: string | undefined;
  dateSelectorValue: TDateSelectValues;
  initResultsFilter: 'allOrders' | 'myOrders';
  openAddReceiptDrawer: boolean;
  orderHistoryData: IOrderHistoryResponse | undefined;
  orderHistoryError: AxiosError | undefined;
  orderHistoryLoaded: boolean;
  localDeliveryData?: IOrderHistoryResponse | undefined;
  localDeliveryError?: AxiosError | undefined;
  localDeliveryLoaded?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setLocalDeliveryInfo?: Dispatch<SetStateAction<any>>;
  pHRequest: IPHRequest;
  lDRequest: ILDRequest;
  setAlertState: Dispatch<SetStateAction<IAlert | null>>;
  allOrderDetails: IOrderDetailsResponse[] | [];
  setAllOrderDetails: Dispatch<SetStateAction<IOrderDetailsResponse[] | []>>;
  setLocalDeliveryAlertState: Dispatch<SetStateAction<IAlert | null>>;
  setDateSelectorValue: Dispatch<SetStateAction<TDateSelectValues>>;
  setOpenAddReceiptDrawer: Dispatch<SetStateAction<boolean>>;
  setPHRequest: Dispatch<SetStateAction<IPHRequest>>;
  setLDRequest: Dispatch<SetStateAction<ILDRequest>>;
  setViewType: Dispatch<SetStateAction<string>>;
  viewType: string;
  rules: TAccessPolicy[];
  isPHFilterLoading: boolean;
  setPHFilterRequest: Dispatch<SetStateAction<IPHFilterRequest>>;
  triggerPHFilterFetch?: () => void;
  cpsFilterError: boolean;
  poJobFilters: string[];
  paymentFilters: { last4: string; cardType: string }[];
}

const defaultState: IPurchaseHistoryContext = {
  alertState: null,
  localDeliveryAlertState: null,
  customerRole: '',
  customerType: '',
  cpsDataSource: undefined,
  dateSelectorValue: 'recentOrders',
  initResultsFilter: 'myOrders',
  openAddReceiptDrawer: false,
  orderHistoryData: undefined,
  orderHistoryError: undefined,
  orderHistoryLoaded: false,
  localDeliveryData: undefined,
  localDeliveryError: undefined,
  localDeliveryLoaded: false,
  setLocalDeliveryInfo: () => null,
  pHRequest: {
    pageSize: 20,
    pageNumber: 1,
    startDate: DateRanges.recentOrders.startDate,
    endDate: DateRanges.recentOrders.endDate,
  },
  lDRequest: {
    startDate: DateRanges.recentOrders.startDate,
    endDate: DateRanges.recentOrders.endDate,
    pageNumber: 1,
    pageSize: 20,
  },
  setPHFilterRequest: () => null,
  setAlertState: () => null,
  allOrderDetails: [],
  setAllOrderDetails: () => null,
  setLocalDeliveryAlertState: () => null,
  setDateSelectorValue: () => null,
  setOpenAddReceiptDrawer: () => null,
  setPHRequest: () => null,
  setLDRequest: () => null,
  setViewType: () => null,
  viewType: 'Card',
  rules: [],
  isPHFilterLoading: false,
  triggerPHFilterFetch: () => null,
  cpsFilterError: false,
  poJobFilters: [],
  paymentFilters: [],
};

// Create the context (internal only, not exported for Fast Refresh compatibility)
export const PurchaseHistoryContext =
  createContext<IPurchaseHistoryContext>(defaultState);
